// UAT / Pilot Acceptance readiness service.
//
// Reads the geo-api UAT contract (/api/admin/uat/*) through the SAME geoApi
// client and path convention already proven by investorService — no /api
// prefix in the path (the client owns the base), so there is zero risk of the
// /api/api duplication regression and no apiClient changes are required.
//
// Every read is defensive:
//   - never throws (callers can `await` without try/catch and still render)
//   - returns safe empty defaults when the backend is silent or unauthorized
//   - never surfaces raw backend errors (geoApi already normalizes those)
//
// Authorization is enforced server-side; the UI additionally gates the panel
// to authorized roles so unauthorized users never even issue the request.

import { geoApi } from '@/lib/geoApi';

/** Coarse readiness state shared across every UAT check. */
export type UatReadinessState =
  | 'ready'
  | 'partial'
  | 'blocked'
  | 'not_available' // backend has not reported this area yet
  | 'unauthorized'; // caller is not permitted to view this area

/** Stable identifiers for each cross-application readiness area. */
export type UatAreaKey =
  | 'investor_onboarding'
  | 'kyc'
  | 'loi'
  | 'nda'
  | 'payment_validation'
  | 'mou'
  | 'document_workflow'
  | 'gis_access'
  | 'portal_api';

export interface UatReadinessArea {
  key: UatAreaKey;
  /** Human label (UI provides a default if the server omits it). */
  label?: string;
  state: UatReadinessState;
  /** 0..100 completeness for the area when the backend provides it. */
  score?: number;
  /** Short explanation suitable for a smart empty state. */
  note?: string;
  /** Recommended next step / action wording from the backend, if any. */
  nextStep?: string;
  updatedAt?: string;
}

export interface UatReadinessSummary {
  /** Overall pilot acceptance posture. */
  overall: UatReadinessState;
  /** 0..100 aggregate readiness across reported areas. */
  overallScore: number;
  areas: UatReadinessArea[];
  /** geo-api requestId echoed for support/diagnostics (never an error). */
  requestId?: string;
  generatedAt?: string;
}

/** Canonical ordering + default labels for the nine UAT areas. */
export const UAT_AREA_ORDER: UatAreaKey[] = [
  'investor_onboarding',
  'kyc',
  'loi',
  'nda',
  'payment_validation',
  'mou',
  'document_workflow',
  'gis_access',
  'portal_api',
];

export const UAT_AREA_LABELS: Record<UatAreaKey, string> = {
  investor_onboarding: 'Investor Onboarding',
  kyc: 'KYC Readiness',
  loi: 'LOI Readiness',
  nda: 'NDA Readiness',
  payment_validation: 'Payment Validation',
  mou: 'MOU Readiness',
  document_workflow: 'Document Workflow',
  gis_access: 'GIS Access',
  portal_api: 'Portal ↔ API',
};

const EMPTY_SUMMARY: UatReadinessSummary = {
  overall: 'not_available',
  overallScore: 0,
  areas: [],
};

type BackendAcceptance = 'completed' | 'pending' | 'blocked' | 'unknown';

interface BackendItem {
  code?: string;
  label?: string;
  status?: BackendAcceptance | string;
  acceptance?: BackendAcceptance | string;
}

interface BackendUatResponse {
  overall?: BackendAcceptance | string;
  items?: BackendItem[];
}

function toPortalState(value?: string): UatReadinessState {
  switch (value) {
    case 'completed':
    case 'ready':
      return 'ready';

    case 'pending':
    case 'degraded':
    case 'unknown':
      return 'partial';

    case 'blocked':
    case 'not_ready':
      return 'blocked';

    default:
      return 'not_available';
  }
}

function scoreForState(state: UatReadinessState): number {
  switch (state) {
    case 'ready':
      return 100;
    case 'partial':
      return 50;
    case 'blocked':
      return 0;
    default:
      return 0;
  }
}

function findBackendItem(
  response: BackendUatResponse | undefined,
  code: string,
): BackendItem | undefined {
  return Array.isArray(response?.items)
    ? response.items.find((item) => item?.code === code)
    : undefined;
}

function itemState(item?: BackendItem): UatReadinessState {
  const value =
    typeof item?.acceptance === 'string'
      ? item.acceptance
      : typeof item?.status === 'string'
        ? item.status
        : undefined;

  return toPortalState(value);
}

export const uatService = {
  async getReadiness(): Promise<UatReadinessSummary> {
    const [crossRes, workflowRes, gisRes] = await Promise.all([
      geoApi.get<BackendUatResponse>('/admin/uat/cross-app'),
      geoApi.get<BackendUatResponse>('/admin/uat/workflow'),
      geoApi.get<BackendUatResponse>('/admin/uat/gis'),
    ]);

    const statuses = [
      crossRes.status,
      workflowRes.status,
      gisRes.status,
    ];

    if (
      statuses.every(
        (status) => status === 401 || status === 403,
      )
    ) {
      return {
        ...EMPTY_SUMMARY,
        overall: 'unauthorized',
      };
    }

    const portalApi = itemState(
      findBackendItem(crossRes.data, 'portal_api'),
    );

    const investorWorkflow = itemState(
      findBackendItem(crossRes.data, 'investor_workflow'),
    );

    const kyc = itemState(
      findBackendItem(workflowRes.data, 'kyc'),
    );

    const loi = itemState(
      findBackendItem(workflowRes.data, 'LOI'),
    );

    const nda = itemState(
      findBackendItem(workflowRes.data, 'NDA'),
    );

    const payment = itemState(
      findBackendItem(workflowRes.data, 'PAYMENT_VALIDATION'),
    );

    const mou = itemState(
      findBackendItem(workflowRes.data, 'MOU'),
    );

    const gisState = toPortalState(gisRes.data?.overall);

    const areas: UatReadinessArea[] = [
      {
        key: 'investor_onboarding',
        state: investorWorkflow,
        score: scoreForState(investorWorkflow),
      },
      {
        key: 'kyc',
        state: kyc,
        score: scoreForState(kyc),
      },
      {
        key: 'loi',
        state: loi,
        score: scoreForState(loi),
      },
      {
        key: 'nda',
        state: nda,
        score: scoreForState(nda),
      },
      {
        key: 'payment_validation',
        state: payment,
        score: scoreForState(payment),
      },
      {
        key: 'mou',
        state: mou,
        score: scoreForState(mou),
      },
      {
        key: 'document_workflow',
        state: investorWorkflow,
        score: scoreForState(investorWorkflow),
      },
      {
        key: 'gis_access',
        state: gisState,
        score: scoreForState(gisState),
      },
      {
        key: 'portal_api',
        state: portalApi,
        score: scoreForState(portalApi),
      },
    ];

    const reported = areas.filter(
      (area) => area.state !== 'not_available',
    );

    if (reported.length === 0) {
      return EMPTY_SUMMARY;
    }

    const overall: UatReadinessState =
      reported.some((area) => area.state === 'blocked')
        ? 'blocked'
        : reported.every((area) => area.state === 'ready')
          ? 'ready'
          : 'partial';

    const overallScore = Math.round(
      reported.reduce(
        (sum, area) => sum + scoreForState(area.state),
        0,
      ) / reported.length,
    );

    return {
      overall,
      overallScore,
      areas,
      generatedAt: new Date().toISOString(),
    };
  },
};

/* ───────────────── pure helpers (never throw) ───────────────── */

/** Merge server-reported areas onto the canonical 9-area template so the panel
 * always renders every area in a stable order — missing areas degrade to a
 * safe `not_available` state instead of disappearing. */
export function normalizeUatAreas(
  summary: UatReadinessSummary | null,
): UatReadinessArea[] {
  const byKey = new Map<UatAreaKey, UatReadinessArea>();
  (summary?.areas ?? []).forEach((a) => {
    if (a && UAT_AREA_ORDER.includes(a.key)) byKey.set(a.key, a);
  });
  return UAT_AREA_ORDER.map((key) => {
    const server = byKey.get(key);
    return {
      key,
      label: server?.label || UAT_AREA_LABELS[key],
      state: server?.state ?? 'not_available',
      score: server?.score,
      note: server?.note,
      nextStep: server?.nextStep,
      updatedAt: server?.updatedAt,
    };
  });
}

export interface UatStateMeta {
  label: string;
  tone: 'ready' | 'partial' | 'blocked' | 'neutral';
}

export function uatStateMeta(s: UatReadinessState): UatStateMeta {
  switch (s) {
    case 'ready': return { label: 'Ready', tone: 'ready' };
    case 'partial': return { label: 'In progress', tone: 'partial' };
    case 'blocked': return { label: 'Blocked', tone: 'blocked' };
    case 'unauthorized': return { label: 'Restricted', tone: 'neutral' };
    default: return { label: 'Not reported', tone: 'neutral' };
  }
}

/** Default explanation + recommended step for a not-yet-reported area, so the
 * UI can show a smart empty state without inventing backend errors. */
export function uatAreaFallback(area: UatReadinessArea): { explanation: string; nextStep: string } {
  if (area.state === 'unauthorized') {
    return {
      explanation: 'You do not have permission to view this readiness area.',
      nextStep: 'Sign in with an authorized administrator account to review pilot acceptance.',
    };
  }
  return {
    explanation: `${area.label ?? 'This area'} has not been reported by geo-api yet.`,
    nextStep: 'Run the corresponding UAT check in geo-api, then refresh this panel.',
  };
}
